/**
 * Staff Roles Configuration
 *
 * Defines roles for installers (specialists) and dealers,
 * plus mapping from free-text roles to standard roles.
 *
 * NEW: Unified role system (user_companies.role)
 * - admin: Full access including company/group management
 * - manager: Can view data and take actions (settle/void)
 * - technician: Can view data only
 * - viewer: Can view data only
 */

// NEW: Unified roles for all user types (stored in user_companies.role)
export const UNIFIED_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Full access, all locations' },
  { value: 'admin', label: 'Admin', description: 'Full access including company/group management' },
  { value: 'manager', label: 'Manager', description: 'Can view data and take actions (settle/void)' },
  { value: 'technician', label: 'Technician', description: 'Can view data only' },
  { value: 'viewer', label: 'Viewer', description: 'Can view data only' }
];

// Get badge color for role (works for both installer and dealer roles)
export function getUnifiedRoleBadgeColor(role) {
  switch (role) {
    // Installer roles
    case 'owner': return 'dark';
    case 'technician': return 'success';
    case 'viewer': return 'secondary';
    // Shared roles
    case 'admin': return 'danger';
    case 'manager': return 'primary';
    // Dealer roles
    case 'finance': return 'info';
    case 'reporting': return 'warning';
    case 'contact': return 'secondary';
    default: return 'secondary';
  }
}

// Company type enum — matches server `user_companies.company_type`.
// Group/location sub-scoping is derived from `user_companies.location_id`:
//   installer + location_id = 0     → all locations under the installer
//   installer + location_id > 0     → pinned to that single location
//   dealer    + location_id = 0     → all dealers in the group
//   dealer    + location_id > 0     → pinned to that single dealer
export const COMPANY_TYPES = {
  DEALER: 'dealer',
  INSTALLER: 'installer'
};

// Assignment type options surfaced in the staff admin form.
// "Scope" is captured separately as a location_id (0 = all, otherwise the
// specific location_id for installer rows or dealer_id for dealer rows).
export const ASSIGNMENT_TYPES = [
  { value: 'installer', label: 'Installer', description: 'Access to one installer (all locations or a single location)' },
  { value: 'dealer',    label: 'Dealer',    description: 'Access to one dealer group (all dealerships or a single dealership)' }
];

// LEGACY: Installer (Specialist) roles - kept for backward compatibility
export const INSTALLER_ROLES = [
  { value: 'technician', label: 'Technician', description: 'Sees only their assigned work in the "My Work" view' },
  { value: 'manager', label: 'Manager', description: 'Can assign work, manage jobs, and view all shop activity' },
  { value: 'owner', label: 'Owner/Operator', description: 'Full access including staff management and settings' }
];

// Dealer roles (stored in user_companies.role)
export const DEALER_ROLES = [
  { value: 'manager', label: 'Manager', description: 'Any function for the dealer group and associated dealerships' },
  { value: 'admin', label: 'Admin', description: 'Any function at dealer group user assigned to' },
  { value: 'finance', label: 'Finance', description: 'Dealer function at assigned dealership' },
  { value: 'reporting', label: 'Reporting', description: 'Reports and related at assigned dealer group' },
  { value: 'contact', label: 'Contact', description: 'No access but related at assigned dealership' }
];

/**
 * Map common free-text role values to standard dealer roles
 * Used when displaying/editing contacts that were created with free-text roles
 */
const DEALER_ROLE_MAPPINGS = {
  // Sales variations
  'sales': 'sales',
  'salesperson': 'sales',
  'sales person': 'sales',
  'salesman': 'sales',
  'saleswoman': 'sales',
  'sales rep': 'sales',
  'sales representative': 'sales',
  'sales associate': 'sales',
  'sales consultant': 'sales',
  'sales advisor': 'sales',
  'internet sales': 'sales',
  'bdc': 'sales',
  'bdc rep': 'sales',
  'bdc agent': 'sales',

  // Finance variations
  'finance': 'finance',
  'finance manager': 'finance',
  'f&i': 'finance',
  'f&i manager': 'finance',
  'finance and insurance': 'finance',
  'business manager': 'finance',
  'deal closer': 'finance',

  // Manager variations
  'manager': 'manager',
  'general manager': 'manager',
  'gm': 'manager',
  'sales manager': 'manager',
  'desk manager': 'manager',
  'floor manager': 'manager',
  'owner': 'manager',
  'dealer': 'manager',
  'principal': 'manager',
  'director': 'manager',

  // Service variations -> map to manager for now (they have broader access)
  'service advisor': 'manager',
  'service writer': 'manager',
  'service manager': 'manager',
  'parts manager': 'manager',
  'fixed ops': 'manager',
  'fixed operations': 'manager'
};

/**
 * Map a free-text role to a standard dealer role
 * @param {string} freeTextRole - The role string from the database
 * @returns {string|null} - The standard role value, or null if no mapping found
 */
export function mapToDealerRole(freeTextRole) {
  if (!freeTextRole) return null;

  const normalized = freeTextRole.toLowerCase().trim();
  return DEALER_ROLE_MAPPINGS[normalized] || null;
}

/**
 * Get the display label for a role
 * @param {string} roleValue - The role value (e.g., 'sales', 'technician')
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {string} - The display label
 */
export function getRoleLabel(roleValue, userType) {
  const roles = userType === 'dealer' ? DEALER_ROLES : INSTALLER_ROLES;
  const role = roles.find(r => r.value === roleValue);
  return role ? role.label : roleValue || 'Staff';
}

/**
 * Get roles array for a user type
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {Array} - Array of role objects
 */
export function getRolesForUserType(userType) {
  return userType === 'dealer' ? DEALER_ROLES : INSTALLER_ROLES;
}

/**
 * Get the UNIFIED role options for a user type — used to populate the role
 * dropdown so it matches the company assigned. Dealer → dealer role set;
 * installer (or unset) → the full unified installer roles (owner/admin/
 * manager/technician/viewer), NOT the 3-value legacy INSTALLER_ROLES.
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {Array} - Array of { value, label, description }
 */
export function getUnifiedRolesForType(userType) {
  return userType === 'dealer' ? DEALER_ROLES : UNIFIED_ROLES;
}

/**
 * Check if a role value is valid for a user type
 * @param {string} roleValue - The role to check
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {boolean}
 */
export function isValidRole(roleValue, userType) {
  const roles = getRolesForUserType(userType);
  return roles.some(r => r.value === roleValue);
}

/**
 * Check if a role is a valid role for any user type
 * @param {string} roleValue - The role to check
 * @param {string} userType - Optional: 'installer' or 'dealer' to check specific set
 * @returns {boolean}
 */
export function isValidUnifiedRole(roleValue, userType) {
  if (userType === 'dealer') return DEALER_ROLES.some(r => r.value === roleValue);
  if (userType === 'installer') return UNIFIED_ROLES.some(r => r.value === roleValue);
  // No userType: check both
  return UNIFIED_ROLES.some(r => r.value === roleValue) || DEALER_ROLES.some(r => r.value === roleValue);
}

/**
 * NEW: Map legacy role to unified role
 * @param {string} legacyRole - The old role value
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {string} - The unified role value
 */
export function mapLegacyToUnifiedRole(legacyRole, userType) {
  if (!legacyRole) return 'viewer';

  const normalized = legacyRole.toLowerCase().trim();

  // If already a unified role, return as-is
  if (isValidUnifiedRole(normalized)) {
    return normalized;
  }

  // Map installer legacy roles
  if (userType === 'installer') {
    switch (normalized) {
      case 'owner': return 'admin';
      case 'manager': return 'manager';
      case 'technician': return 'technician';
      default: return 'viewer';
    }
  }

  // Map dealer legacy roles
  if (userType === 'dealer') {
    switch (normalized) {
      case 'manager': return 'manager';
      case 'admin': return 'admin';
      case 'finance': return 'finance';
      case 'sales': return 'finance'; // legacy sales → finance
      case 'reporting': return 'reporting';
      default: return 'contact';
    }
  }

  return 'contact';
}

/**
 * Get role label (checks both installer and dealer roles)
 * @param {string} roleValue - The role value
 * @param {string} userType - Optional: 'installer' or 'dealer'
 * @returns {string} - The display label
 */
export function getUnifiedRoleLabel(roleValue, userType) {
  if (userType === 'dealer') {
    const role = DEALER_ROLES.find(r => r.value === roleValue);
    return role ? role.label : roleValue || 'Contact';
  }
  const role = UNIFIED_ROLES.find(r => r.value === roleValue) || DEALER_ROLES.find(r => r.value === roleValue);
  return role ? role.label : roleValue || 'Viewer';
}
